// NotificationDropdown.js
import { connect } from "react-redux";
import React, { useEffect, useMemo, useState } from "react";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import { useDetectClickOutside } from "react-detect-click-outside";

import { NOTIFICATION_TYPES } from "../../../system/globalEnums";
import ProductImage from "../../../assets/images/productimage.png";
import { storeActions } from "../../../redux/actions/store.action";
import {
  buttonNameConstants,
  shopifyConstants,
  systemConstants,
} from "../../../constants";
import TradePrintLabelModal from "../../../views/Inventory/Items/printingLabelModal/TradePrintLabelModal";
import {
  CopyIcon,
  TickIcon,
  NotificationIcon,
} from "../../../assets/icons/iconsProvider";
import {
  getShortestSku,
  getReceiptPrintDate,
  getProductLabelNotifications,
  isShopifyConnected,
  handlePrintShopifyPackingSlips,
  isSpinnerEnabled,
  LoadingIndicator,
} from "../../utility";
import { shopifyActions, systemActions } from "../../../redux/actions";
import ConfirmationModal from "../../../views/Inventory/Items/ConfirmationModal/ConfirmationModal";
import { shopifyService } from "../../../services";

const NoNotifications = () => (
  <div className="d-flex justify-content-center align-items-center p-3 bg-light rounded m-0 w-100">
    No Unread Notifications
  </div>
);

const NotificationMenu = ({
  spinnerArray,
  currentStore,
  activateSpinner,
  userNotifications,
  isShowShopifyOrderNotificationModal,
  deactivateSpinner,
  getUserNotifications,
  markNotificationAsRead,
  getShopifyOrderNotificationModalDetails,
}) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [copiedSku, setCopiedSku] = useState(null);
  const [orderDetail, setOrderDetail] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isPrintLabelModal, setIsPrintLabelModal] = useState(false);
  const [isShowOrderDetailModal, setIsShowOrderDetailModal] = useState(false);
  const [isConfirmationModal, setIsConfirmationModal] = useState(false);
  const [shopifyViewOrderOpened, setShopifyViewOrderOpened] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    message: "",
    type: "",
  });

  const toggleDropdown = () => setShowDropdown(!showDropdown);
  const togglePrintLabelModal = () => setIsPrintLabelModal(!isPrintLabelModal);

  const ref = useDetectClickOutside({
    onTriggered: () => {
      // Prevent closing dropdown if Shopify tab is active
      if (isShopifyConnected(currentStore) && shopifyViewOrderOpened) {
        if(!isShowShopifyOrderNotificationModal){
          setShopifyViewOrderOpened(false);
        }
        return;
      }
      setShowDropdown(false);
    },
  });

  // Modern copy-to-clipboard
  const copyToClipboard = async (skuNumber) => {
    try {
      await navigator.clipboard.writeText(skuNumber);
      setCopiedSku(skuNumber);
      setTimeout(() => setCopiedSku(null), 3000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleNotifcationItemClick = (userNotificationId) => {
    markNotificationAsRead(currentStore?.id, [userNotificationId]);
  };

  const handleMarkAllAsRead = (notifications) => {
    if (notifications?.length > 0) {
      const ids = notifications.map((item) => item.id);
      markNotificationAsRead(currentStore?.id, ids);
    }
  };

  const handlePrintLabelClick = (data) => {
    setSelectedRows(data);
    togglePrintLabelModal();
  };

  const printAllLabel = (labelNotifications) => {
    if (labelNotifications?.length > 0) {
      togglePrintLabelModal();
      setSelectedRows(labelNotifications.flatMap((obj) => obj.products));
    }
  };

  const printAllShopifyPackingSlips = async () => {
    if (shopifyNotifications?.length > 0) {
      activateSpinner(shopifyConstants.PRINT_ALL_SHOPIFY_ORDERS_SLIPS);
      // Group notifications by sku[0]
      const skuMap = {};
      shopifyNotifications.forEach((notification) => {
        const sku = notification.sku?.[0];
        if (sku) {
          if (!skuMap[sku]) skuMap[sku] = [];
          skuMap[sku].push(notification);
        }
      });

      // For each SKU, pick the latest notification
      const latestNotifs = Object.values(skuMap).map((notifs) =>
        notifs.reduce((latest, notif) =>
          new Date(notif.createdOn) > new Date(latest.createdOn)
            ? notif
            : latest
        )
      );

      if (latestNotifs.length > 0) {
        const orderIds = latestNotifs.map((notif) => notif.sku[0]);
        try {
          const orders = await shopifyService.getMultiplyShopifyOrdersById(
            currentStore?.id,
            orderIds
          );
          const ordersArray = Array.isArray(orders) ? orders : [orders];
          handlePrintShopifyPackingSlips(ordersArray, currentStore);
        } catch (error) {
          console.error("Failed to fetch Shopify orders:", error);
        }
        deactivateSpinner(shopifyConstants.PRINT_ALL_SHOPIFY_ORDERS_SLIPS);
      }
    }
  };

  const handleViewOrderClick = async (notification) => {
    setShopifyViewOrderOpened(true);
    getShopifyOrderNotificationModalDetails(
      currentStore?.id,
      notification.sku[0],
      notification.products
    );
  };

  // Memoized categorized notifications
  const {
    customerNotifications,
    labelNotifications,
    shopifyNotifications,
    otherNotifications,
  } = useMemo(() => {
    const customers =
      userNotifications?.filter(
        (n) => n.type === NOTIFICATION_TYPES.CUSTOMER
      ) || [];

    const labels = getProductLabelNotifications(userNotifications).map(
      (item) => ({
        ...item,
        products: item.products.map((p) => ({ ...p, labelType: item.type })),
      })
    );

    const shopify =
      userNotifications?.filter((n) => n.type === NOTIFICATION_TYPES.SHOPIFY) ||
      [];

    const others =
      userNotifications
        ?.filter((n) => n.type === NOTIFICATION_TYPES.PRODUCT_LABEL_IMPORT)
        .map((item) => ({
          ...item,
          products: item.products.map((p) => ({ ...p, labelType: item.type })),
        })) || [];

    return {
      customerNotifications: customers,
      labelNotifications: labels,
      shopifyNotifications: shopify,
      otherNotifications: others,
    };
  }, [userNotifications]);

  // Map tab index to notifications, handling Shopify tab presence
  const tabMapping = useMemo(() => {
    if (isShopifyConnected(currentStore)) {
      return {
        0: customerNotifications,
        1: labelNotifications,
        2: shopifyNotifications,
        3: otherNotifications,
      };
    }
    return {
      0: customerNotifications,
      1: labelNotifications,
      2: otherNotifications,
    };
  }, [
    customerNotifications,
    labelNotifications,
    shopifyNotifications,
    otherNotifications,
    currentStore,
  ]);

  const openConfirmModal = () => {
    let message = "Are you sure you want to dismiss all notifications?";
    if (tabIndex === 0) {
      message = "Do you want to dismiss all customer notifications?";
    } else if (tabIndex === 1) {
      message = "Do you want to dismiss all label notifications?";
    } else if (isShopifyConnected(currentStore) && tabIndex === 2) {
      message = "Do you want to dismiss all Shopify notifications?";
    } else {
      message = "Do you want to dismiss all other notifications?";
    }
    setModalConfig({ message, type: "" });
    setIsConfirmationModal(true);
  };

  const handleYesButtonClick = () => {
    const notifications = tabMapping[tabIndex] || [];
    handleMarkAllAsRead(notifications);
    setIsConfirmationModal(false);
  };

  const handleNoButtonClick = () => {
    setIsConfirmationModal(false);
  };

  useEffect(() => {
    if (currentStore?.id) {
      getUserNotifications(currentStore.id);
      setTabIndex(0); // Reset to first tab on store change
    }
  }, [currentStore]);

  return (
    <>
      <div className="notification-container" ref={ref}>
        <button
          onClick={toggleDropdown}
          className={`notification-toggle-button ${
            showDropdown ? "active-notification-toggle-button" : ""
          }`}
          aria-label="Toggle notifications"
        >
          <NotificationIcon />
          {userNotifications.length > 0 && (
            <div
              className={`notification-count ${
                userNotifications.length >
                systemConstants.MAX_NOTIFICATION_NUMBER
                  ? "max-notification-count"
                  : ""
              }`}
            >
              {userNotifications.length >
              systemConstants.MAX_NOTIFICATION_NUMBER
                ? `${systemConstants.MAX_NOTIFICATION_NUMBER}+`
                : userNotifications.length}
            </div>
          )}
        </button>

        {showDropdown && (
          <div className="notification-dropdown">
            <div className="d-flex justify-content-between align-items-center w-100 py-2">
              <div className="py-2 notification-heading">Notifications</div>
            </div>

            <Tabs selectedIndex={tabIndex} onSelect={(i) => setTabIndex(i)}>
              <TabList>
                <Tab>
                  Customers <span>{customerNotifications.length}</span>
                </Tab>
                <Tab>
                  Labels <span>{labelNotifications.length}</span>
                </Tab>
                {isShopifyConnected(currentStore) && (
                  <Tab>
                    Shopify <span>{shopifyNotifications.length}</span>
                  </Tab>
                )}
                <Tab>
                  Other <span>{otherNotifications.length}</span>
                </Tab>
              </TabList>

              {/* Customers */}
              <TabPanel>
                <div className="d-flex justify-content-between align-items-center w-100 py-2">
                  <button
                    type="button"
                    className="ms-auto mark-all-as-read-button"
                    onClick={openConfirmModal}
                    disabled={customerNotifications.length === 0}
                  >
                    {buttonNameConstants.DISMISS_ALL}
                  </button>
                </div>

                <div className="notifications-wrapper container-scrollbar">
                  {customerNotifications.length > 0 ? (
                    customerNotifications.map((n, i) => (
                      <div
                        key={i}
                        className={`notification-item ${
                          n.isRead ? "read-notification-item" : ""
                        }`}
                      >
                        <img src={n.imgUrl || ProductImage} alt="Product" />
                        <div className="product-notification-content-container">
                          <p>{n.product_name}</p>
                          <div className="sku-number-text">
                            <p>{getShortestSku(n.sku)}</p>
                            <button
                              type="button"
                              onClick={() =>
                                copyToClipboard(getShortestSku(n.sku))
                              }
                              className="copy-button"
                              aria-label="Copy SKU"
                            >
                              <CopyIcon />
                            </button>
                            {copiedSku === getShortestSku(n.sku) && (
                              <span>Copied!</span>
                            )}
                          </div>
                          <p>{n.message}</p>
                        </div>
                        <button
                          type="button"
                          className="mb-auto mark-as-read-button"
                          onClick={() => handleNotifcationItemClick(n.id)}
                          aria-label="Mark as read"
                        >
                          <TickIcon className="read-icon" />
                        </button>
                      </div>
                    ))
                  ) : (
                    <NoNotifications />
                  )}
                </div>
              </TabPanel>

              {/* Labels */}
              <TabPanel>
                <div className="d-flex justify-content-end align-items-center w-100 py-2">
                  <button
                    type="button"
                    className="mark-all-as-read-button"
                    onClick={() => printAllLabel(labelNotifications)}
                    disabled={labelNotifications.length === 0}
                  >
                    {buttonNameConstants.PRINT_ALL}
                  </button>
                  <button
                    type="button"
                    className="mark-all-as-read-button"
                    onClick={openConfirmModal}
                    disabled={labelNotifications.length === 0}
                  >
                    {buttonNameConstants.DISMISS_ALL}
                  </button>
                </div>

                <div className="notifications-wrapper container-scrollbar">
                  {labelNotifications.length > 0 ? (
                    labelNotifications.map((n, i) => (
                      <div
                        key={i}
                        className={`notification-item ${
                          n.isRead ? "read-notification-item" : ""
                        }`}
                      >
                        <div className="product-notification-content-container w-100">
                          <div className="d-flex flex-column gap-2 align-items-start justify-content-between w-100">
                            <div className="d-flex justify-content-between align-items-start w-100">
                              <p className="print-message-text">{n.message}</p>
                              <button
                                type="button"
                                className="mb-auto mark-as-read-button ms-auto"
                                onClick={() => handleNotifcationItemClick(n.id)}
                                aria-label="Mark as read"
                              >
                                <TickIcon className="read-icon" />
                              </button>
                            </div>
                            <div className="d-flex justify-content-between align-items-end w-100">
                              <button
                                onClick={() =>
                                  handlePrintLabelClick(n.products)
                                }
                                className="notification-print-button"
                              >
                                Print Labels
                              </button>
                              <span className="notification-date-time-text">
                                {getReceiptPrintDate(n.createdOn)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <NoNotifications />
                  )}
                </div>
              </TabPanel>

              {/* Shopify */}
              {isShopifyConnected(currentStore) && (
                <TabPanel>
                  <div className="d-flex justify-content-end align-items-center w-100 py-2">
                    <button
                      type="button"
                      className="mark-all-as-read-button"
                      onClick={printAllShopifyPackingSlips}
                      disabled={
                        shopifyNotifications.length === 0 ||
                        isSpinnerEnabled(
                          spinnerArray,
                          shopifyConstants.PRINT_ALL_SHOPIFY_ORDERS_SLIPS
                        )
                      }
                    >
                      {isSpinnerEnabled(
                        spinnerArray,
                        shopifyConstants.PRINT_ALL_SHOPIFY_ORDERS_SLIPS
                      ) ? (
                        <LoadingIndicator />
                      ) : (
                        buttonNameConstants.PRINT_ALL
                      )}
                    </button>
                    <button
                      type="button"
                      className="mark-all-as-read-button"
                      onClick={openConfirmModal}
                      disabled={shopifyNotifications.length === 0}
                    >
                      {buttonNameConstants.DISMISS_ALL}
                    </button>
                  </div>

                  <div className="notifications-wrapper container-scrollbar">
                    {shopifyNotifications.length > 0 ? (
                      shopifyNotifications.map((n, i) => (
                        <div
                          key={i}
                          className={`notification-item ${
                            n.isRead ? "read-notification-item" : ""
                          }`}
                        >
                          <div className="product-notification-content-container w-100">
                            <div className="d-flex flex-column gap-2 align-items-start justify-content-between w-100">
                              <div className="d-flex justify-content-between align-items-start w-100">
                                <p
                                  className={`print-message-text ${
                                    n.products?.length ? "text-danger" : ""
                                  }`}
                                >
                                  {n.message}
                                </p>
                                <button
                                  type="button"
                                  className="mb-auto mark-as-read-button ms-auto"
                                  onClick={() =>
                                    handleNotifcationItemClick(n.id)
                                  }
                                  aria-label="Mark as read"
                                >
                                  <TickIcon className="read-icon" />
                                </button>
                              </div>
                              <div className="d-flex justify-content-between align-items-end w-100">
                                <button
                                  onClick={() => handleViewOrderClick(n)}
                                  className="notification-print-button"
                                >
                                  View Order
                                </button>
                                <span className="notification-date-time-text">
                                  {getReceiptPrintDate(n.createdOn)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <NoNotifications />
                    )}
                  </div>
                </TabPanel>
              )}

              {/* Other */}
              <TabPanel>
                <div className="d-flex justify-content-between align-items-center w-100 py-2">
                  <button
                    type="button"
                    className="ms-auto mark-all-as-read-button"
                    onClick={openConfirmModal}
                    disabled={otherNotifications.length === 0}
                  >
                    {buttonNameConstants.DISMISS_ALL}
                  </button>
                </div>

                <div className="notifications-wrapper container-scrollbar">
                  {otherNotifications.length > 0 ? (
                    otherNotifications.map((n, i) => (
                      <div
                        key={i}
                        className={`notification-item ${
                          n.isRead ? "read-notification-item" : ""
                        }`}
                      >
                        <div className="w-100">
                          <div className="d-flex flex-column gap-2 align-items-start justify-content-between w-100">
                            <div className="d-flex justify-content-between align-items-start w-100">
                              <p className="print-message-text other-print-message">
                                {n.message}
                              </p>
                              <button
                                type="button"
                                className="mb-auto mark-as-read-button ms-auto"
                                onClick={() => handleNotifcationItemClick(n.id)}
                                aria-label="Mark as read"
                              >
                                <TickIcon className="read-icon" />
                              </button>
                            </div>
                            <p className="notification-date-time-text mb-0">
                              {getReceiptPrintDate(n.createdOn)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <NoNotifications />
                  )}
                </div>
              </TabPanel>
            </Tabs>
          </div>
        )}
      </div>

      {isPrintLabelModal && (
        <TradePrintLabelModal
          printLabelModal={isPrintLabelModal}
          togglePrintLabelModal={togglePrintLabelModal}
          selectedRows={selectedRows}
        />
      )}
      {isConfirmationModal && (
        <ConfirmationModal
          isConfirmationModal={isConfirmationModal}
          toggleConfirmationModal={() => setIsConfirmationModal(false)}
          message={modalConfig.message}
          handleYesButtonClick={handleYesButtonClick}
          handleNoButtonClick={handleNoButtonClick}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  spinnerArray: state.system.spinnerArray,
  userNotifications: state.store.userNotifications,
  currentStore: state.store.currentStore,
  isShowShopifyOrderNotificationModal:
    state.shopify.isShowShopifyOrderNotificationModal,
  shopifyOrderNotificationModalDetails:
    state.shopify.shopifyOrderNotificationModalDetails,
});
const mapDispatchToProps = (dispatch) => ({
  activateSpinner: (data) => dispatch(systemActions.activateSpinner(data)),
  deactivateSpinner: (data) => dispatch(systemActions.deactivateSpinner(data)),
  getUserNotifications: (storeId) =>
    dispatch(storeActions.getUserNotifications(storeId)),
  markNotificationAsRead: (storeId, data) =>
    dispatch(storeActions.markNotificationAsRead(storeId, data)),
  getShopifyOrderNotificationModalDetails: (
    storeId,
    orderId,
    refundedItemIds
  ) =>
    dispatch(
      shopifyActions.getShopifyOrderNotificationModalDetails(
        storeId,
        orderId,
        refundedItemIds
      )
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(NotificationMenu);
